import i18n from '@/plugins/i18n';
import * as types from './mutations-type.js';
import RolesService from "../../../../services/RolesService";
import LanguajeService from "../../../../services/LanguajeService";
import { normalizeDomains } from "../utils/domainUtils";

export default {
    loadingON({commit, state}) {
        commit(types.SET_LOADING_ON, true);
    },
    loadingOFF({commit}) {
        commit(types.SET_LOADING_OFF, false);
    },
    async saveRoles({commit, state}, [item, toast]) {
        try {
            // Normalize domains before saving to prevent duplicates
            if (item && Array.isArray(item.domains)) {
                item.domains = normalizeDomains(item.domains);
            }
            
            await RolesService.save(item);
            toast.success(i18n.t('roles.notification.save'), {
                queueable: true,
                icon: 'mdi-check-circle',
            });
        } catch (error) {
            if (error.message.includes('406')) {
                toast.info(i18n.t('roles.notification.inUse'), {
                    queueable: true,
                    icon: "mdi-alert-circle",
                });
            } else if (error.message !== 'cancelRequest') {
                toast.error(i18n.t('roles.notification.saveError'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
            }
        }
    },
    async fetchListModules({dispatch, commit, state}, [toast]) {
        dispatch('loadingON');
        try {
            // Call the actual API instead of using mock data
            const { data } = await RolesService.modules();
            
            // Process modules data
            const modules = Array.isArray(data) ? data : [];
            
            // Extract fields by domain for easier access
            const moduleFields = modules.reduce((acc, module) => {
                acc[module.domain] = module.fields || [];
                return acc;
            }, {});
            
            commit(types.SET_MODULE_LIST, modules);
            commit(types.SET_MODULE_FIELDS, moduleFields);
            dispatch('loadingOFF');
        } catch (error) {
            dispatch('loadingOFF');
            if (error.message !== 'cancelRequest') {
                toast.error(i18n.t('roles.notification.listError'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
            }
        }
    },

    async fetchListRoles({dispatch, commit, state}, [profile, toast]) {
        state.roles = [];
        dispatch('loadingON');
        try {
            const {data} = await RolesService.roles(profile);

            // Transform the roles data structure to what the UI expects
            let processedRoles = [];
            
            for (let role of data) {
                // Create permissionByDomainDTOList from domains
                let permissionByDomainList = [];
                
                // Group permissions by domain to avoid duplicates
                const domainMap = new Map();
                
                if (role.domains && role.domains.length) {
                    for (const domainData of role.domains) {
                        const domain = domainData.domain;
                        
                        if (!domainMap.has(domain)) {
                            domainMap.set(domain, {
                                module: domain,
                                permission: [...domainData.permissions]
                            });
                        } else {
                            // Merge permissions for the same domain
                            const existingPerms = domainMap.get(domain).permission;
                            for (const perm of domainData.permissions) {
                                if (!existingPerms.includes(perm)) {
                                    existingPerms.push(perm);
                                }
                            }
                        }
                    }
                    
                    // Convert map values to array
                    permissionByDomainList = Array.from(domainMap.values());
                }
                
                processedRoles.push({
                    id: role.id,
                    canDeleted: role.canDeleted,
                    language_key: role.language_key,
                    permissionByDomainDTOList: permissionByDomainList
                });
            }

            commit(types.SET_ROLE_LIST, processedRoles);
            dispatch('loadingOFF');
        } catch (error) {
            if (error.message !== 'cancelRequest')
                toast.error(i18n.t('roles.notification.listError'), {
                    icon: "mdi-alert-circle",
                    queueable: true
                });
        }
    },
    async deleteRol({commit, state}, [id, toast]) {
        try {
            await RolesService.delete(id);
            toast.success(i18n.t('roles.notification.deleted'), {
                queueable: true,
                icon: 'mdi-check-circle',
            });
        } catch (error) {
            if (error.message.includes('406')) {
                toast.info(i18n.t('roles.notification.inUse'), {
                    queueable: true,
                    icon: "mdi-alert-circle",
                });
            } else {
                if (error.message !== 'cancelRequest')
                    toast.error(i18n.t('roles.notification.deletedError'), {
                        queueable: true
                    });
            }
        }
    },
    async fetchRole({commit}, id) {
        try {
            const { data } = await RolesService.getRole(id);
            
            // Normalize domains to prevent duplicates using shared utility
            const domains = normalizeDomains(data.domains || []);
            
            // Transform the response to the required format
            // Note: We keep the original language_key intact for backend compatibility
            const role = {
                id: data.id,
                name: data.language_key && data.language_key.length > 0 ? 
                    LanguajeService.getKey3(data.language_key) : '',
                canDeleted: data.canDeleted,
                language_key: data.language_key || [],
                domains: domains
            };
            
            return role;
        } catch (error) {
            console.error('Error fetching role:', error);
            throw error;
        }
    }
}
